// ECharts theme + palette for the v2 dashboard, mapped from the Ringil design tokens
// (tailwind.config.js → theme.extend.colors). Keep colors here in sync with those tokens so
// charts match the rest of the UI. Used by RingilChart via echarts.registerTheme('ringil', …).

export const chartColors = {
  brand: '#FE5000',
  brandMedium: '#FFEEE5',
  primary: '#343333',
  secondary: '#616061',
  tertiary: '#B2B2B2',
  quaternary: '#E7EBEE',
  gridLine: '#E7EBEE',
  success: '#00C65E',
  successMedium: '#99E8BF',
  processing: '#00B5E2',
  processingMedium: '#B2E9F6',
  draft: '#4A25AA',
  warning: '#FFD100',
  error: '#E4002B',
  errorMedium: '#F9CDD5',
} as const;

// Default categorical series palette (brand first, then complementary hues).
export const chartPalette = [chartColors.brand, chartColors.processing, chartColors.draft, chartColors.success, chartColors.warning, chartColors.secondary];

// Shared ECharts data-label config for bar series. Labels render only on non-zero columns
// (zero/null/undefined are blanked) so charts aren't cluttered with "0"s. `format` turns a
// non-zero value into its display string (count, duration, value + unit, …).
export const barValueLabel = (format: (value: number) => string, position: 'top' | 'right' = 'top') => ({
  show: true,
  position,
  color: chartColors.secondary,
  formatter: (p: { value: number | null }) => (!p.value ? '' : format(p.value)),
});

// Tooltip param shape for axis-trigger tooltips (only the fields we use).
interface AxisTooltipParam {
  marker?: string;
  seriesName?: string;
  seriesIndex: number;
  value: number | null;
  axisValueLabel?: string;
}

// Builds an axis-trigger tooltip formatter so the hover popup renders each series value with the
// SAME rounding used elsewhere (axis labels, KPI cards, bar value-labels) instead of echarts'
// raw full-precision default. `format` turns a value into its display string; it receives the
// series name + index so a chart with mixed units (e.g. currency bars + a count line) can format
// each series differently.
export const axisTooltipFormatter =
  (format: (value: number, seriesName: string, seriesIndex: number) => string) =>
  (params: AxisTooltipParam | AxisTooltipParam[]): string => {
    const items = Array.isArray(params) ? params : [params];
    if (!items.length) return '';
    const header = `<div style="margin-bottom:4px;font-weight:500;color:${chartColors.primary}">${items[0].axisValueLabel ?? ''}</div>`;
    const rows = items
      .map(
        p =>
          `<div style="display:flex;align-items:center;gap:6px">${p.marker ?? ''}<span style="flex:1">${p.seriesName ?? ''}</span>` +
          `<span style="font-weight:600">${format(Number(p.value ?? 0), p.seriesName ?? '', p.seriesIndex)}</span></div>`,
      )
      .join('');
    return header + rows;
  };

const axisCommon = {
  axisLine: { show: false },
  axisTick: { show: false },
  axisLabel: { color: chartColors.secondary, fontFamily: 'DM Sans' },
  splitLine: { lineStyle: { color: chartColors.gridLine } },
};

// Theme object passed to echarts.registerTheme('ringil', …).
export const ringilChartTheme = {
  color: chartPalette,
  backgroundColor: 'transparent',
  textStyle: { fontFamily: 'DM Sans, sans-serif', color: chartColors.secondary },
  title: { textStyle: { color: chartColors.primary, fontFamily: 'DM Sans', fontWeight: 500 } },
  categoryAxis: { ...axisCommon, splitLine: { show: false, lineStyle: { color: chartColors.gridLine } } },
  valueAxis: { ...axisCommon, axisLabel: { color: chartColors.tertiary, fontFamily: 'DM Sans' } },
  logAxis: axisCommon,
  timeAxis: axisCommon,
  legend: { textStyle: { color: chartColors.secondary, fontFamily: 'DM Sans' }, icon: 'roundRect', itemWidth: 10, itemHeight: 10 },
  tooltip: {
    backgroundColor: '#FFFFFF',
    borderColor: chartColors.quaternary,
    borderWidth: 1,
    textStyle: { color: chartColors.primary, fontFamily: 'DM Sans', fontSize: 12 },
    extraCssText: 'box-shadow: 0px 2px 8px 0px rgba(0,0,0,0.08); border-radius: 4px;',
  },
} as const;

export const RINGIL_CHART_THEME_NAME = 'ringil';